// src/contexts/LanguageContext.js
// VEGA ROBOTICS - Sistema de Internacionalização (i18n)

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { translations, defaultLanguage, languages } from '../locales';

// ============================================================
// CONTEXT
// ============================================================
const LanguageContext = createContext(null);

// ============================================================
// STORAGE KEY
// ============================================================
const LANGUAGE_STORAGE_KEY = 'vega_language';

// ============================================================
// HELPER: Detecta idioma do navegador
// ============================================================
const detectBrowserLanguage = () => {
  if (typeof window === 'undefined') return defaultLanguage;
  
  const browserLang = navigator.language || navigator.userLanguage;
  const shortLang = browserLang?.split('-')[0]?.toLowerCase();
  
  // Verifica se o idioma do navegador está disponível
  const availableCodes = languages.map(l => l.code);
  if (availableCodes.includes(shortLang)) {
    return shortLang;
  }
  
  return defaultLanguage;
};

// ============================================================
// HELPER: Obtém idioma salvo ou detectado
// ============================================================
const getInitialLanguage = () => {
  if (typeof window === 'undefined') return defaultLanguage;
  
  // Primeiro tenta obter do localStorage
  const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (savedLanguage && translations[savedLanguage]) {
    return savedLanguage;
  }
  
  // Se não houver salvo, detecta do navegador
  return detectBrowserLanguage();
};

// ============================================================
// PROVIDER
// ============================================================
export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getInitialLanguage);
  const [isLoading, setIsLoading] = useState(true);

  // Inicialização
  useEffect(() => {
    const initialLang = getInitialLanguage();
    setLanguageState(initialLang);
    updateDocumentLanguage(initialLang);
    setIsLoading(false);
  }, []);

  // Atualiza o atributo lang do documento HTML
  const updateDocumentLanguage = (lang) => {
    if (typeof document !== 'undefined') {
      const langInfo = languages.find(l => l.code === lang);
      document.documentElement.lang = langInfo?.locale || lang;
    }
  };

  // Função para mudar o idioma
  const setLanguage = useCallback((newLanguage) => {
    if (!translations[newLanguage]) {
      console.warn(`Language "${newLanguage}" not available. Available: ${Object.keys(translations).join(', ')}`);
      return;
    }

    setLanguageState(newLanguage);
    
    // Salva no localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, newLanguage);
    }
    
    // Atualiza o documento
    updateDocumentLanguage(newLanguage);
    
    // Dispara evento customizado (útil para analytics, etc.)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('languageChange', { 
        detail: { language: newLanguage } 
      }));
    }
  }, []);

  // Traduções atuais
  const currentTranslations = useMemo(() => {
    return translations[language] || translations[defaultLanguage];
  }, [language]);

  // Informações do idioma atual
  const currentLanguageInfo = useMemo(() => {
    return languages.find(l => l.code === language) || languages[0];
  }, [language]);

  // ============================================================
  // FUNÇÃO t() - TRADUÇÃO COM SUPORTE A INTERPOLAÇÃO
  // ============================================================
  const t = useCallback((key, params = {}) => {
    // Divide a chave por pontos para acessar objetos aninhados
    // Ex: 'nav.home' -> translations.nav.home
    const keys = key.split('.');
    let value = currentTranslations;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Chave não encontrada - retorna a própria chave
        console.warn(`Translation key "${key}" not found for language "${language}"`);
        return key;
      }
    }

    // Se o valor não é string, retorna como está (pode ser array ou objeto)
    if (typeof value !== 'string') {
      return value;
    }

    // Interpolação de parâmetros
    // Ex: t('footer.copyright', { year: 2024 }) -> "© 2024 Vega..."
    let result = value;
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      result = result.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(paramValue));
    });

    return result;
  }, [currentTranslations, language]);

  // ============================================================
  // FUNÇÃO tArray() - PARA ARRAYS DE TRADUÇÕES
  // ============================================================
  const tArray = useCallback((key) => {
    const value = t(key);
    return Array.isArray(value) ? value : [value];
  }, [t]);

  // ============================================================
  // FUNÇÃO hasTranslation() - VERIFICA SE CHAVE EXISTE
  // ============================================================
  const hasTranslation = useCallback((key) => {
    const keys = key.split('.');
    let value = currentTranslations;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return false;
      }
    }

    return true;
  }, [currentTranslations]);

  // ============================================================
  // CONTEXT VALUE
  // ============================================================
  const contextValue = useMemo(() => ({
    // Estado
    language,
    languages,
    currentLanguageInfo,
    isLoading,
    
    // Funções de tradução
    t,
    tArray,
    hasTranslation,
    
    // Controle de idioma
    setLanguage,
    
    // Acesso direto às traduções (para casos especiais)
    translations: currentTranslations,
  }), [language, currentLanguageInfo, isLoading, t, tArray, hasTranslation, setLanguage, currentTranslations]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// ============================================================
// HOOK useTranslation
// ============================================================
export const useTranslation = () => {
  const context = useContext(LanguageContext);
  
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  
  return context;
};

// ============================================================
// HOOK useLanguage (alias simplificado)
// ============================================================
export const useLanguage = () => {
  const { language, setLanguage, languages, currentLanguageInfo } = useTranslation();
  return { language, setLanguage, languages, currentLanguageInfo };
};

// ============================================================
// HOC withTranslation (para componentes de classe)
// ============================================================
export const withTranslation = (WrappedComponent) => {
  return function WithTranslationComponent(props) {
    const translationProps = useTranslation();
    return <WrappedComponent {...props} {...translationProps} />;
  };
};

// ============================================================
// COMPONENTE Trans (para tradução inline com componentes)
// ============================================================
export const Trans = ({ i18nKey, components = {}, values = {} }) => {
  const { t } = useTranslation();
  const translation = t(i18nKey, values);
  
  if (typeof translation !== 'string') {
    return translation;
  }

  // Parse simples para substituir <0>, <1>, etc. por componentes
  // Ex: <Trans i18nKey="footer.madeWith" components={{ heart: <span>❤️</span> }} />
  let result = translation;
  Object.entries(components).forEach(([key, component]) => {
    const regex = new RegExp(`<${key}>(.*?)</${key}>`, 'g');
    const matches = result.match(regex);
    
    if (matches) {
      // Por simplicidade, retornamos a string sem os componentes inline
      // Para casos complexos, considere usar react-i18next
      result = result.replace(regex, (match, content) => content);
    }
  });

  return result;
};

export default LanguageContext;
